import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../SEOHead';
import RelatedLinks from './RelatedLinks';

const WebsiteFeedbackTool = () => {
  return (
    <>
    <SEOHead
      title="Website Feedback Tool for Teams"
      description="Leave visual feedback directly on live websites. Pin notes, assign tasks, and resolve issues. Invisible to visitors. Free to start."
      path="/website-feedback-tool"
    />
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="border-b border-ink-faint/20 bg-cream/80 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="text-base font-display font-semibold tracking-tight text-ink">Cayda</Link>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-3 py-1.5 text-xs font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free</Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-3xl mx-auto px-6 pt-16 pb-12">
        <p className="text-xs font-mono uppercase tracking-widest text-ink-muted mb-4">Website Feedback Tool</p>
        <h1 className="text-3xl sm:text-4xl tracking-tight leading-tight mb-5">
          <span className="font-serif italic text-ink/80">Stop collecting feedback</span>{' '}
          <span className="font-display font-bold text-ink">in five different places.</span>
        </h1>
        <p className="text-base font-display text-ink-secondary leading-relaxed max-w-xl">
          Cayda lets your team leave notes directly on live websites. Click anywhere, type a comment, and it stays pinned to that exact spot. No screenshots. No Slack threads. No guessing which page someone meant.
        </p>
      </div>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-6 pb-20">
        <div className="space-y-14 text-[15px] font-display text-ink-secondary leading-relaxed">

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">The feedback loop is broken</h2>
            <p className="mb-3">
              You know the pattern. Someone screenshots a page, circles something in red, drops it in Slack. Someone else replies asking which page. A third person emails the same issue to a different thread. A week later, the bug is still there because the developer never saw any of it.
            </p>
            <p>
              Website feedback shouldn't require a workflow diagram. It should happen where the work is, on the page itself.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Feedback that lives on the page</h2>
            <p className="mb-3">
              With Cayda, your team logs into your live site, clicks the annotation button, and clicks on any element to leave a note. That note stays anchored to the element, not a screenshot of it, not a description of where it is, but the actual DOM element on the actual page.
            </p>
            <p>
              Every note captures context automatically: the page URL, viewport size, browser, OS, and a screenshot. No one has to remember to include that information. It's just there.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Invisible to your visitors</h2>
            <p>
              This is what makes Cayda different from most feedback tools. The annotation layer is completely invisible to anyone who isn't authenticated. No badges, no widgets, no "powered by" branding. Your visitors see your site exactly as you designed it. Your team sees the feedback layer on top of it.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">A dashboard for triage, not just collection</h2>
            <p className="mb-3">
              Notes don't just pile up. The Cayda dashboard gives you a centralized view of every note across all your sites, with filters for status, priority, assignee, and note type. You can view notes as a list or a Kanban board, bulk update statuses, and export everything to CSV.
            </p>
            <p>
              Each note has a type (bug, design, content, question, or general feedback) with color coded markers that make it easy to spot patterns at a glance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Built for teams, not individuals</h2>
            <p>
              Invite teammates as collaborators. Assign notes to specific people. Reply in threaded comments, right on the page. Everyone works from the same source of truth, the live site, so there's no confusion about versions, environments, or which Figma frame maps to which URL.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-4">What's included</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { title: 'Click to annotate', desc: 'Pin notes to any element on any page. Notes stay anchored even if the layout shifts.' },
                { title: 'Auto screenshots', desc: 'Every note includes a screenshot of the page at the moment it was created.' },
                { title: 'Browser & device capture', desc: 'Viewport size, browser, OS, and device type are logged automatically.' },
                { title: 'Threaded comments', desc: 'Reply to notes in context. Conversations stay attached to the element.' },
                { title: 'Status & priority', desc: 'Open, in progress, resolved. Low, medium, high, critical.' },
                { title: 'Assignment', desc: 'Assign notes to team members. They get notified by email.' },
                { title: 'Note types', desc: 'Bug, design, content, question, or feedback. Color coded markers on the page.' },
                { title: 'Slack & Jira', desc: 'Push notes to Slack channels and create Jira issues with one click.' },
              ].map(({ title, desc }) => (
                <div key={title} className="bg-white border border-ink-faint/20 rounded-xl p-4">
                  <p className="text-sm font-display font-semibold text-ink mb-1">{title}</p>
                  <p className="text-xs font-display text-ink-muted leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Works on every platform</h2>
            <p>
              One script tag. WordPress and Shopify both offer one click installs with no code required. For everything else, Squarespace, Webflow, Wix, Next.js, React, and plain HTML, you paste a single line of code. The script loads asynchronously and adds no meaningful performance overhead for your visitors. It handles single page apps, page transitions, and dynamic content automatically.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-display font-semibold text-ink mb-3">Free to start. Simple to scale.</h2>
            <p className="mb-3">
              The free plan includes 2 websites, 50 notes per site, and 3 team members. No credit card required. When your team grows, Pro is $9/month base plus $5 per member (billed annually) for unlimited sites, notes, file attachments, and Slack integration. Team is $14/month base plus $6 per member (billed annually) and adds screen recording, guest access, and Jira/Asana integrations.
            </p>
            <p>
              There's no per page pricing, no usage caps on views, and no surprise charges. You pay for the team members who actively collaborate.
            </p>
          </section>
        </div>

        <div className="mt-14"><RelatedLinks current="/website-feedback-tool" /></div>

        {/* CTA */}
        <div className="mt-16 pt-12 border-t border-ink-faint/20 text-center">
          <h3 className="text-xl font-display font-bold text-ink tracking-tight mb-2">Try it on your site.</h3>
          <p className="text-sm font-display text-ink-muted mb-5">Set up takes 30 seconds. No credit card required.</p>
          <Link to="/sign-up" className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all">Get started free →</Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-ink-faint/20 bg-cream">
        <div className="max-w-3xl mx-auto px-6 py-8 flex items-center justify-between">
          <p className="text-xs font-display text-ink-faint">© {new Date().getFullYear()} Slowfast LLC</p>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Privacy</Link>
            <Link to="/terms" className="text-xs font-display text-ink-faint hover:text-ink-secondary transition-colors">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default WebsiteFeedbackTool;
